import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'
import {
  hasCuratedPublishReasonCopy,
  humanizePublishReason,
} from '@/shell/ide-v2/components/publish-gate-copy'
import type { StudioProjectReadinessChatContext } from './project-readiness-chat-context'
import { hasStudioChatMutationIntent } from './studio-chat-mutation-intent'

const READINESS_TOPIC =
  /canonical\s+readiness|캐노니컬\s*레디니스|프로젝트\s*(?:상태|준비)|(?:게시|발행)(?:를|을)?\s*막는|(?:게시|발행)\s*준비|project\s+(?:status|readiness)|publish(?:ing)?\s+(?:status|readiness|blockers?)|プロジェクトの?(?:状態|準備)|公開を妨げ|发布(?:状态|准备|阻碍)|發佈(?:狀態|準備|阻礙)/i

interface ReadinessCopy {
  heading: string
  loadFailed: string
  noProject: string
  project: string
  generationComplete: string
  generationUnproved: string
  actionFlow: string
  preview: string
  publish: string
  ready: string
  blocked: string
  yes: string
  no: string
  blockers: string
  additional: (count: number) => string
  noBlockers: string
  boundary: string
}

const COPY: Record<LocaleCode, ReadinessCopy> = {
  en: {
    heading: 'Canonical readiness for the current project',
    loadFailed: 'The current project could not be loaded, so its readiness cannot be reported as current.',
    noProject: 'No project is currently open. Open a project to review its canonical readiness.',
    project: 'Project',
    generationComplete: 'Generation workflow: completion is recorded',
    generationUnproved: 'Generation workflow: no completion evidence is recorded',
    actionFlow: 'ActionFlow readiness',
    preview: 'Preview ready',
    publish: 'Publish readiness',
    ready: 'ready',
    blocked: 'blocked',
    yes: 'yes',
    no: 'no',
    blockers: 'Canonical publish blockers',
    additional: (count) => `${count} additional canonical blocker(s) are recorded; they are not inferred here.`,
    noBlockers: 'No canonical publish blockers are currently recorded.',
    boundary: 'This is Studio editor preflight only; it does not prove release authorization, deployment, or a successful publish.',
  },
  ko: {
    heading: '현재 프로젝트의 canonical readiness',
    loadFailed: '현재 프로젝트를 불러오지 못해 준비 상태를 최신 정보로 보고할 수 없습니다.',
    noProject: '현재 열려 있는 프로젝트가 없습니다. 프로젝트를 연 뒤 canonical readiness를 확인하세요.',
    project: '프로젝트',
    generationComplete: '생성 워크플로: 완료 기록이 있습니다',
    generationUnproved: '생성 워크플로 완료 증거가 없습니다',
    actionFlow: 'ActionFlow 준비 상태',
    preview: '미리보기 준비',
    publish: '게시 준비 상태',
    ready: '준비됨',
    blocked: '차단됨',
    yes: '가능',
    no: '불가',
    blockers: 'canonical 게시 차단 항목',
    additional: (count) => `추가 canonical 차단 항목 ${count}개가 기록되어 있으며 여기서는 추정해 나열하지 않습니다.`,
    noBlockers: '현재 기록된 canonical 게시 차단 항목은 없습니다.',
    boundary: '이 결과는 Studio 편집기의 사전 점검이며 릴리스 승인, 배포 또는 게시 성공을 증명하지 않습니다.',
  },
  ja: {
    heading: '現在のプロジェクトの canonical readiness',
    loadFailed: '現在のプロジェクトを読み込めないため、準備状況を最新情報として報告できません。',
    noProject: '現在開いているプロジェクトはありません。プロジェクトを開いて canonical readiness を確認してください。',
    project: 'プロジェクト',
    generationComplete: '生成ワークフロー: 完了記録あり',
    generationUnproved: '生成ワークフロー: 完了の証拠なし',
    actionFlow: 'ActionFlow の準備状況',
    preview: 'プレビュー準備',
    publish: '公開準備状況',
    ready: '準備完了',
    blocked: 'ブロック中',
    yes: '可能',
    no: '不可',
    blockers: 'canonical 公開ブロッカー',
    additional: (count) => `追加の canonical ブロッカーが ${count} 件あります。ここでは推測して列挙しません。`,
    noBlockers: '現在記録されている canonical 公開ブロッカーはありません。',
    boundary: 'これは Studio エディターの事前確認であり、リリース承認、デプロイ、公開成功の証明ではありません。',
  },
  zh: {
    heading: '当前项目的 canonical readiness',
    loadFailed: '当前项目加载失败，因此无法将准备状态作为最新信息报告。',
    noProject: '当前没有打开的项目。请打开项目后查看 canonical readiness。',
    project: '项目',
    generationComplete: '生成工作流：已有完成记录',
    generationUnproved: '生成工作流：没有完成证据',
    actionFlow: 'ActionFlow 准备状态',
    preview: '预览准备',
    publish: '发布准备状态',
    ready: '已准备',
    blocked: '已阻止',
    yes: '可用',
    no: '不可用',
    blockers: 'canonical 发布阻止项',
    additional: (count) => `另有 ${count} 个 canonical 阻止项；此处不会推测列出。`,
    noBlockers: '当前没有记录 canonical 发布阻止项。',
    boundary: '这只是 Studio 编辑器预检，不证明发布授权、部署或发布成功。',
  },
  'zh-TW': {
    heading: '目前專案的 canonical readiness',
    loadFailed: '目前專案載入失敗，因此無法將準備狀態視為最新資訊回報。',
    noProject: '目前沒有開啟的專案。請開啟專案後查看 canonical readiness。',
    project: '專案',
    generationComplete: '生成工作流程：已有完成記錄',
    generationUnproved: '生成工作流程：沒有完成證據',
    actionFlow: 'ActionFlow 準備狀態',
    preview: '預覽準備',
    publish: '發佈準備狀態',
    ready: '已準備',
    blocked: '已阻擋',
    yes: '可用',
    no: '不可用',
    blockers: 'canonical 發佈阻擋項',
    additional: (count) => `另有 ${count} 個 canonical 阻擋項；此處不會推測列出。`,
    noBlockers: '目前沒有記錄 canonical 發佈阻擋項。',
    boundary: '這只是 Studio 編輯器預檢，不證明發佈授權、部署或發佈成功。',
  },
}

export function isStudioProjectReadinessQuestion(text: string): boolean {
  return READINESS_TOPIC.test(text) && !hasStudioChatMutationIntent(text)
}

const DYNAMIC_VALUE_COPY: Record<LocaleCode, Record<string, string>> = {
  en: {
    draft: 'draft', active: 'active', published: 'published', archived: 'archived', unknown: 'unknown',
    saved: 'saved', unsaved: 'not saved', saving: 'saving', failed: 'failed', conflict: 'conflict',
    ready: 'ready', gap: 'not ready', pending: 'pending', error: 'error', blocked: 'blocked',
  },
  ko: {
    draft: '초안', active: '활성', published: '게시됨', archived: '보관됨', unknown: '알 수 없음',
    saved: '저장됨', unsaved: '저장되지 않음', saving: '저장 중', failed: '실패', conflict: '충돌',
    ready: '준비됨', gap: '준비되지 않음', pending: '대기 중', error: '오류', blocked: '차단됨',
  },
  ja: {
    draft: '下書き', active: '有効', published: '公開済み', archived: 'アーカイブ済み', unknown: '不明',
    saved: '保存済み', unsaved: '未保存', saving: '保存中', failed: '失敗', conflict: '競合',
    ready: '準備完了', gap: '未準備', pending: '保留中', error: 'エラー', blocked: 'ブロック中',
  },
  zh: {
    draft: '草稿', active: '有效', published: '已发布', archived: '已归档', unknown: '未知',
    saved: '已保存', unsaved: '未保存', saving: '保存中', failed: '失败', conflict: '冲突',
    ready: '已准备', gap: '未准备', pending: '等待中', error: '错误', blocked: '已阻止',
  },
  'zh-TW': {
    draft: '草稿', active: '啟用', published: '已發佈', archived: '已封存', unknown: '未知',
    saved: '已儲存', unsaved: '未儲存', saving: '儲存中', failed: '失敗', conflict: '衝突',
    ready: '已準備', gap: '未準備', pending: '等待中', error: '錯誤', blocked: '已阻擋',
  },
}

const KNOWN_REASON_COPY: ReadonlyArray<{
  prefix: string
  copy: Record<LocaleCode, string>
}> = [
  {
    prefix: 'Generated app readiness gate is not publish-ready.',
    copy: {
      en: "The app isn't finished building yet",
      ko: '앱 제작이 아직 끝나지 않았습니다',
      ja: 'アプリの生成がまだ完了していません',
      zh: '应用生成尚未完成',
      'zh-TW': '應用程式生成尚未完成',
    },
  },
  {
    prefix: 'Generated UI evidence is incomplete.',
    copy: {
      en: 'Generated UI evidence is incomplete',
      ko: '생성된 UI 증거가 불완전합니다',
      ja: '生成された UI の証拠が不完全です',
      zh: '生成的 UI 证据不完整',
      'zh-TW': '生成的 UI 證據不完整',
    },
  },
  {
    prefix: 'ActionFlow evidence is incomplete.',
    copy: {
      en: 'ActionFlow evidence is incomplete',
      ko: 'ActionFlow 증거가 불완전합니다',
      ja: 'ActionFlow の証拠が不完全です',
      zh: 'ActionFlow 证据不完整',
      'zh-TW': 'ActionFlow 證據不完整',
    },
  },
  {
    prefix: 'Datasource runtime evidence is incomplete.',
    copy: {
      en: 'Datasource runtime evidence is incomplete',
      ko: '데이터소스 런타임 증거가 불완전합니다',
      ja: 'データソース実行時の証拠が不完全です',
      zh: '数据源运行时证据不完整',
      'zh-TW': '資料來源執行階段證據不完整',
    },
  },
]

function localizeDynamicValue(value: string, locale: LocaleCode): string {
  return DYNAMIC_VALUE_COPY[locale][value] ?? DYNAMIC_VALUE_COPY[locale].unknown
}

function presentReason(
  reason: string,
  locale: LocaleCode,
  index: number,
): string {
  const known = KNOWN_REASON_COPY.find(({ prefix }) => reason.trim().startsWith(prefix))
  if (known) return known.copy[locale]
  if (locale === 'en') return humanizePublishReason(reason, locale).title
  if (locale === 'ko' && hasCuratedPublishReasonCopy(reason)) {
    return humanizePublishReason(reason, locale).title
  }
  const ordinal = index + 1
  return locale === 'ko'
    ? `발행 화면에서 확인할 canonical 항목 ${ordinal}`
    : locale === 'ja'
      ? `公開画面で確認する canonical 項目 ${ordinal}`
      : locale === 'zh'
        ? `请在发布页面确认的 canonical 项目 ${ordinal}`
        : `請在發佈頁面確認的 canonical 項目 ${ordinal}`
}

export function formatStudioProjectReadinessChatResponse(
  context: StudioProjectReadinessChatContext | null,
  locale: LocaleCode,
): string {
  const copy = COPY[locale]
  if (!context) return `${copy.heading}\n\n${copy.noProject}`
  if (context.project.loadState === 'failed') {
    return `${copy.heading}\n\n${copy.loadFailed}`
  }

  const reasons = context.publish.primaryReasons.map((reason, index) =>
    `${index + 1}. ${presentReason(reason, locale, index)}`,
  )
  const blockerSection = reasons.length > 0
    ? [`${copy.blockers}:`, ...reasons]
    : [copy.noBlockers]
  if (context.publish.remainingReasonCount > 0) {
    blockerSection.push(copy.additional(context.publish.remainingReasonCount))
  }

  return [
    copy.heading,
    '',
    `- ${copy.project}: ${localizeDynamicValue(context.project.lifecycleStatus, locale)} · ${localizeDynamicValue(context.project.saveState, locale)}`,
    `- ${context.project.durableGenerationState === 'complete' ? copy.generationComplete : copy.generationUnproved}`,
    `- ${copy.actionFlow}: ${localizeDynamicValue(context.generation.actionFlowStatus, locale)}`,
    `- ${copy.preview}: ${context.publish.previewReady ? copy.yes : copy.no}`,
    `- ${copy.publish}: ${context.publish.publishReady ? copy.ready : copy.blocked}`,
    '',
    ...blockerSection,
    '',
    copy.boundary,
  ].join('\n')
}
